import { API_BASE_URL } from "src/config/apiBase";
import React, { useState, useEffect } from "react";
import Select from "react-select";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "../../CSS/Global.css";
import "../Equipments/ProductFormPage.css";
import { Eye, EyeOff, X } from "lucide-react";
import { decryptToken } from "../utils/getToken";
import { getDecryptedUserFromCookie } from "../utils/getCookie";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const safeParse = (value, fallback = "") => {
    if (!value) return fallback;

    try {
        let parsed = value;
        // keep parsing if it's still a stringified JSON
        while (typeof parsed === "string" &&
            (parsed.startsWith("{") || parsed.startsWith("[") || parsed.startsWith('"'))) {
            parsed = JSON.parse(parsed);
        }
        return parsed;
    } catch (err) {
        return fallback;
    }
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^[0-9]{10}$/;
const PHONE_MAX_LENGTH = 10;
const NAME_REGEX = /^[a-zA-Z\s.'-]+$/;

const sanitizeDigits = (value, maxLen = 15) =>
    String(value ?? "").replace(/\D/g, "").slice(0, maxLen);

const sanitizeName = (value) =>
    String(value ?? "").replace(/[^a-zA-Z\s.'-]/g, "");

const sanitizeIfsc = (value) =>
    String(value ?? "").replace(/[^a-zA-Z0-9]/gi, "").toUpperCase().slice(0, 11);

const DIGIT_ONLY_FIELDS = new Set(["phone", "emergency_contact_phone", "account_no"]);
const NAME_FIELDS = new Set(["firstName", "lastName"]);
const IFSC_FIELDS = new Set(["ifsc"]);

const NewStaff = () => {
    const apiUrl = API_BASE_URL;

    const location = useLocation();
    const prefill = location.state || {};
    const navigate = useNavigate();
    const isLoggedIn = getDecryptedUserFromCookie("gta_mainstateuser");
    const token = decryptToken("accessToken") || ""
    const [formData, setFormData] = useState({
        firstName: "",
        lastName: "",
        employeeId: "",   // ✅
        dob: "",
        gender: "",
        profilePic: null,
        address: "",
        phone: "",
        email: "",
        password: "",
        emergency_contact_phone: "",
        job_title: "",
        department: "",
        dof_joining: "",
        employ_status: "",
        report_manager: "",
        office_location: "",
        start_time: "",
        end_time: "",
        qualifications: "",
        prev_exp: "",
        skills: [],
        language_known: [],
        account_no: "",
        ifsc: "",   // ✅
        bank: "",   // ✅
        pfNo: "",   // ✅
        marital_status: "",
        hobbies_interest: [],
        weeklyHoliday: [],
    });
    const [preview, setPreview] = useState()
    const [department, setDepartment] = useState([])
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});

    const FieldError = ({ field }) => {
        const message = fieldErrors[field];
        if (!message) return null;
        return (
            <span className="product-form-page__field-hint product-form-page__field-hint--error">
                {message}
            </span>
        );
    };

    const clearFieldError = (field) => {
        setFieldErrors((prev) => {
            if (!prev[field]) return prev;
            const next = { ...prev };
            delete next[field];
            return next;
        });
    };

    const failField = (field, message) => {
        setFieldErrors({ [field]: message });
        toast.error(message, { position: "top-center", autoClose: 4000 });
        const el = document.querySelector(`[data-staff-field="${field}"]`);
        el?.scrollIntoView({ behavior: "smooth", block: "center" });
        return false;
    };

    const daysOptions = [
        { value: "Sunday", label: "Sunday" },
        { value: "Monday", label: "Monday" },
        { value: "Tuesday", label: "Tuesday" },
        { value: "Wednesday", label: "Wednesday" },
        { value: "Thursday", label: "Thursday" },
        { value: "Friday", label: "Friday" },
        { value: "Saturday", label: "Saturday" },
    ];

    const handleChange = (selectedOptions) => {
        // Store only the values in formData
        const selectedDays = selectedOptions ? selectedOptions.map(opt => opt.value) : [];
        setFormData(prev => ({ ...prev, weeklyHoliday: selectedDays }));
    };

    const weeklyHolidayList = Array.isArray(formData.weeklyHoliday)
        ? formData.weeklyHoliday
        : formData.weeklyHoliday
          ? [formData.weeklyHoliday]
          : [];
    const selectedValues = daysOptions.filter((option) => weeklyHolidayList.includes(option.value));

    const fetchDepartment = async () => {
        try {
            const response = await fetch(`${apiUrl}/department`, {
                method: "GET",
                headers: {
                    "Authorization": `Bearer ${token}`
                }
                // credentials: "include"
            })
            const res = await response.json();
            ;
            setDepartment(res || []);
        } catch (error) {
            console.error(error)
        }
    }

    useEffect(() => {
        if (prefill && Object.keys(prefill).length > 0) {
            const parsedSkills = safeParse(prefill.skills, []);
            const parsedLanguages = safeParse(prefill.language_known, []);
            const parsedHobbies = safeParse(prefill.hobbies_interest, []);
            const parsedWeeklyHoliday = safeParse(prefill.weekly_holiday, []);

            setSkills(parsedSkills);
            // setSkillInput(parsedSkills.join(", "));

            setlanguage_known(parsedLanguages);
            // setLanguageInput(parsedLanguages.join(", "));

            setHobbies(parsedHobbies);
            // setHobbyInput(parsedHobbies.join(", "));

            setFormData((prev) => ({
                ...prev,
                firstName: sanitizeName(prefill.firstName || ""),
                lastName: sanitizeName(prefill.lastName || ""),
                dob: prefill.dob || "",
                emergency_contact_phone: sanitizeDigits(prefill.emergency_contact_phone || ""),
                email: prefill.email || "",
                job_title: prefill.job_title || "",
                dof_joining: prefill.dof_joining
                    ? new Date(prefill.dof_joining).toISOString().split("T")[0]
                    : "",
                report_manager: prefill.report_manager || "",
                office_location: prefill.office_location || "",
                start_time: prefill.start_time || "",
                end_time: prefill.end_time || "",
                prev_exp: prefill.prev_exp || "",
                qualifications: prefill.qualifications || "",
                employ_status: prefill.employ_status || "",
                address: prefill.address || "",
                password: prefill.password || "",
                employeeId: prefill.emplyee_ID || "",
                gender: prefill.gender || "",
                department: prefill.department || "",
                phone: sanitizeDigits(prefill.phone || "", PHONE_MAX_LENGTH),
                account_no: sanitizeDigits(prefill.account_no || "", 18),
                ifsc: sanitizeIfsc(prefill.ifsc_code || ""),
                bank: prefill.bank_name || "",
                pfNo: prefill.provident_fund_number || "",
                marital_status: prefill.marital_status || "",
                weeklyHoliday: Array.isArray(parsedWeeklyHoliday)
                    ? parsedWeeklyHoliday
                    : parsedWeeklyHoliday
                      ? [parsedWeeklyHoliday]
                      : [],
            }));


        }
    }, [prefill]);

    useEffect(() => {
        if (prefill && prefill.profilePic) {
            // Normalize slashes for browser
            const normalized = prefill.profilePic.replace(/\\/g, "/");

            // Make sure API URL is prepended if it’s a relative path
            const fullUrl = normalized.startsWith("http")
                ? normalized
                : `${apiUrl}${normalized.startsWith("/") ? "" : "/"}${normalized}`;

            setPreview(fullUrl);
        }
    }, [prefill]);


    // console.log(preview)

    // For dynamic add
    const [skills, setSkills] = useState([]);
    const [skillInput, setSkillInput] = useState("");

    const [language_known, setlanguage_known] = useState([]);
    const [languageInput, setLanguageInput] = useState("");

    const [hobbies_interest, setHobbies] = useState([]);
    const [hobbyInput, setHobbyInput] = useState("");


    const handleInputChange = (e) => {
        const { name, value } = e.target;
        let next = value;

        if (DIGIT_ONLY_FIELDS.has(name)) {
            const maxLen =
                name === "account_no"
                    ? 18
                    : name === "phone" || name === "emergency_contact_phone"
                      ? PHONE_MAX_LENGTH
                      : 15;
            next = sanitizeDigits(value, maxLen);
        } else if (NAME_FIELDS.has(name)) {
            next = sanitizeName(value);
        } else if (IFSC_FIELDS.has(name)) {
            next = sanitizeIfsc(value);
        }

        setFormData((prev) => ({ ...prev, [name]: next }));
        clearFieldError(name);
    };

    const handleFileChange = (e) => {
        setFormData((prev) => ({ ...prev, profilePic: e.target.files[0] }));
    };

    const handleSkillKeyDown = (e) => {
        if (e.key === "," || e.key === "Enter") {
            e.preventDefault();
            if (skillInput.trim() !== "") {
                setSkills([...skills, skillInput.trim()]);
                setSkillInput("");
                clearFieldError("skills");
            }
        }
    };

    const removeSkill = (index) => {
        setSkills(skills.filter((_, i) => i !== index));
        clearFieldError("skills");
    };

    const handleLanguageKeyDown = (e) => {
        if (e.key === "," || e.key === "Enter") {
            e.preventDefault();
            if (languageInput.trim() !== "") {
                setlanguage_known([...language_known, languageInput.trim()]);
                setLanguageInput("");
            }
        }
    };

    const removeLanguage = (index) => {
        setlanguage_known(language_known.filter((_, i) => i !== index));
    };

    const handleHobbyKeyDown = (e) => {
        if (e.key === "," || e.key === "Enter") {
            e.preventDefault();
            if (hobbyInput.trim() !== "") {
                setHobbies([...hobbies_interest, hobbyInput.trim()]);
                setHobbyInput("");
            }
        }
    };

    const removeHobbies = (index) => {
        setHobbies(hobbies_interest.filter((_, i) => i !== index));
    };

    const getFinalSkills = () => {
        const pending = skillInput.trim();
        return pending ? [...skills, pending] : skills;
    };

    const getFinalLanguages = () => {
        const pending = languageInput.trim();
        return pending ? [...language_known, pending] : language_known;
    };

    const isEditMode = Boolean(prefill?.id);

    const validateBeforeSubmit = () => {
        if (!token) {
            toast.error("Session expired. Please log in again.", { position: "top-center" });
            return false;
        }

        setFieldErrors({});

        const firstName = String(formData.firstName || "").trim();
        const lastName = String(formData.lastName || "").trim();
        const email = String(formData.email || "").trim();
        const phone = sanitizeDigits(formData.phone);
        const password = String(formData.password || "").trim();
        const emergencyPhone = sanitizeDigits(formData.emergency_contact_phone);
        const accountNo = sanitizeDigits(formData.account_no, 18);
        const ifsc = sanitizeIfsc(formData.ifsc);
        const bankName = String(formData.bank || "").trim();

        // Order matches the form top → bottom
        if (!firstName) return failField("firstName", "First name is required.");
        if (!NAME_REGEX.test(firstName)) return failField("firstName", "First name can only contain letters.");

        if (!lastName) return failField("lastName", "Last name is required.");
        if (!NAME_REGEX.test(lastName)) return failField("lastName", "Last name can only contain letters.");

        if (!formData.gender) return failField("gender", "Please select a gender.");

        if (!phone) return failField("phone", "Phone number is required.");
        if (!PHONE_REGEX.test(phone)) return failField("phone", "Phone must be exactly 10 digits.");

        if (!email) return failField("email", "Email is required.");
        if (!EMAIL_REGEX.test(email)) return failField("email", "Enter a valid email address.");

        if (!isEditMode && !password) return failField("password", "Password is required for new staff.");
        if (password && password.length < 6) return failField("password", "Password must be at least 6 characters.");

        if (emergencyPhone && !PHONE_REGEX.test(emergencyPhone)) {
            return failField("emergency_contact_phone", "Emergency contact must be exactly 10 digits.");
        }

        if (!String(formData.job_title || "").trim()) return failField("job_title", "Job title is required.");
        if (!String(formData.department || "").trim()) return failField("department", "Department is required.");
        if (!formData.dof_joining) return failField("dof_joining", "Date of joining is required.");
        if (!formData.employ_status) return failField("employ_status", "Employee status is required.");

        if (!String(formData.qualifications || "").trim()) return failField("qualifications", "Qualifications is required.");
        if (!String(formData.prev_exp || "").trim()) return failField("prev_exp", "Experience is required.");
        if (getFinalSkills().length === 0) return failField("skills", "Add at least one skill.");
        if (getFinalLanguages().length === 0) return failField("language_known", "Add at least one language.");

        if (!accountNo) return failField("account_no", "Account number is required.");
        if (accountNo.length < 9) return failField("account_no", "Account number must be at least 9 digits.");

        if (!ifsc) return failField("ifsc", "IFSC code is required.");
        if (ifsc.length !== 11) return failField("ifsc", "IFSC code must be exactly 11 characters.");

        if (!bankName) return failField("bank", "Bank name is required.");

        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateBeforeSubmit()) return;

        const finalSkills = getFinalSkills();
        const finalLanguages = getFinalLanguages();
        const finalHobbies = hobbyInput.trim() ? [...hobbies_interest, hobbyInput.trim()] : hobbies_interest;

        // Use FormData
        const formPayload = new FormData();

        formPayload.append("firstName", formData.firstName);
        formPayload.append("lastName", formData.lastName);
        formPayload.append("full_name", `${formData.firstName} ${formData.lastName}`.trim());
        formPayload.append("dob", formData.dob);
        formPayload.append("gender", formData.gender);
        formPayload.append("email", String(formData.email || "").trim());
        formPayload.append("password", formData.password);
        formPayload.append("phone", sanitizeDigits(formData.phone, PHONE_MAX_LENGTH));
        formPayload.append("qualifications", formData.qualifications);
        formPayload.append("prev_exp", formData.prev_exp);
        formPayload.append("address", formData.address);
        formPayload.append(
            "emergency_contact_phone",
            sanitizeDigits(formData.emergency_contact_phone, PHONE_MAX_LENGTH) || ""
        );
        formPayload.append("username", formData.email || formData.firstName);
        formPayload.append("emplyee_ID", formData.employeeId);
        formPayload.append("job_title", formData.job_title);
        formPayload.append("department", (formData.department));
        formPayload.append("dof_joining", formData.dof_joining);
        formPayload.append("employ_status", formData.employ_status);
        formPayload.append("report_manager", formData.report_manager);
        formPayload.append("office_location", formData.office_location);
        formPayload.append("start_time", formData.start_time);
        formPayload.append("end_time", formData.end_time);
        formPayload.append("account_no", sanitizeDigits(formData.account_no, 18));
        formPayload.append("ifsc_code", sanitizeIfsc(formData.ifsc));
        formPayload.append("bank_name", formData.bank);
        formPayload.append("provident_fund_number", formData.pfNo);
        formPayload.append("marital_status", formData.marital_status);
        formPayload.append(
            "weekly_holiday",
            JSON.stringify(Array.isArray(formData.weeklyHoliday) ? formData.weeklyHoliday : weeklyHolidayList)
        );
        formPayload.append("skills", JSON.stringify(finalSkills));
        formPayload.append("language_known", JSON.stringify(finalLanguages));
        formPayload.append("hobbies_interest", JSON.stringify(finalHobbies));
        formPayload.append("user_name", isLoggedIn?.full_name ?? "");

        // Append image if exists
        if (formData.profilePic) {
            formPayload.append("profilePic", formData.profilePic); // formData.photo should be a File object
        }

        const method = prefill.id ? "PUT" : "POST";
        const url = prefill.id
            ? `${apiUrl}/staff/change/${formData.employeeId}`
            : `${apiUrl}/staff/admin1997`;
        setLoading(true);
        try {
            const res = await fetch(url, {
                method,
                headers: {
                    "Authorization": `Bearer ${token}`, // Do NOT set Content-Type, browser will set it
                },
                body: formPayload,
            });

            const data = await res.json();

            if (res.ok) {
                setFieldErrors({});
                toast.success(
                    prefill.id
                        ? "Team member updated successfully."
                        : "Team member created successfully.",
                    { position: "top-center" }
                );
                navigate('/team-directory');
            } else {
                toast.error(
                    data?.message || data?.error || "Could not save team member. Please try again.",
                    { position: "top-center" }
                );
            }
        } catch (error) {
            toast.error(error?.message || "Network error. Please try again.", {
                position: "top-center",
            });
        } finally {
            setLoading(false);
        }
    };



    useEffect(() => {
        fetchDepartment();
    }, []);

    return (
        <div className="product-form-page">
            <div className="oms-header product-form-page__header">
                <div className="product-form-page__title-wrap">
                    <h1 className="dashboard-title">{isEditMode ? "Edit team member" : "New staff"}</h1>
                    <p className="product-form-page__subtitle">
                        Add or update employee details, role, and payroll—same layout as product forms for a consistent admin experience.
                    </p>
                </div>
                <div className="product-form-page__header-actions">
                    <Link to="/team-directory" className="product-form-page__back">
                        Team directory
                    </Link>
                    {loading ? (
                        <button type="button" className="oms-add-btn" disabled>
                            Saving…
                        </button>
                    ) : (
                        <button type="submit" form="new-staff-form" className="oms-add-btn">
                            {isEditMode ? "Update" : "Save"}
                        </button>
                    )}
                </div>
            </div>

            <form
                id="new-staff-form"
                className="oms-modal-form property-form product-form-page__form"
                onSubmit={handleSubmit}
                noValidate
            >
                <div className="oms-givepaddingfrom">
                    <div className="product-form-page__section">
                        <h2 className="product-form-page__section-title">Basic information</h2>
                        <p className="product-form-page__section-desc">Legal name, demographics, and profile photo.</p>
                    </div>
                    <div className="oms-form-flex">
                    <div className="oms-form-group" data-staff-field="firstName">
                        <label className="oms-form-label">First Name</label>
                        <input
                            type="text"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleInputChange}
                            placeholder="Enter first name"
                        />
                        <FieldError field="firstName" />
                    </div>
                    <div className="oms-form-group" data-staff-field="lastName">
                        <label className="oms-form-label">Last Name</label>
                        <input
                            type="text"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleInputChange}
                            placeholder="Enter last name"
                        />
                        <FieldError field="lastName" />
                    </div>

                    <div className="oms-form-group">
                        <label className="oms-form-label">Date of Birth</label>
                        <input
                            type="date"
                            name="dob"
                            value={formData.dob}
                            onChange={handleInputChange}
                        />
                    </div>
                    {isEditMode && (
                        <div className="oms-form-group">
                            <label className="oms-form-label" htmlFor="employeeId">
                                Employee ID
                            </label>
                            <input
                                id="employeeId"
                                type="text"
                                name="employeeId"
                                value={formData.employeeId}
                                readOnly
                            />
                        </div>
                    )}
                    <div className="oms-form-group" data-staff-field="gender">
                        <label className="oms-form-label">Gender</label>
                        <div className="staff-gender-row">
                            {["Male", "Female", "Other"].map((g) => (
                                <label
                                    key={g}
                                    className={`staff-gender-option${
                                        formData.gender === g ? " staff-gender-option--active" : ""
                                    }`}
                                >
                                    <input
                                        type="radio"
                                        name="gender"
                                        value={g}
                                        checked={formData.gender === g}
                                        onChange={handleInputChange}
                                    />
                                    <span>{g}</span>
                                </label>
                            ))}
                        </div>
                        <FieldError field="gender" />
                    </div>
                    <div className="oms-form-group">
                        <label className="oms-form-label">Profile photo</label>
                        <label className="image-upload-btn">
                            <input
                                type="file"
                                name="profilePic"
                                accept="image/jpeg,image/png,image/webp,image/jpg"
                                hidden
                                onChange={handleFileChange}
                            />
                            Choose photo
                        </label>







                        {preview && (
                            <img src={preview} alt="Profile preview" className="staff-photo-preview" />
                        )}
                    </div>
                </div>

                <div className="product-form-page__section">
                    <h2 className="product-form-page__section-title">Contact</h2>
                    <p className="product-form-page__section-desc">Address, login email, phone, and emergency contact.</p>
                </div>
                <div className="oms-form-flex">
                    <div className="oms-form-group">
                        <label className="oms-form-label">Address</label>
                        <textarea
                            name="address"
                            value={formData.address}
                            onChange={handleInputChange}
                            placeholder="Enter address"
                        />
                    </div>
                    <div className="oms-form-group" data-staff-field="phone">
                        <label className="oms-form-label">Phone</label>
                        <input
                            type="text"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            placeholder="Enter 10-digit phone number"
                            inputMode="numeric"
                            autoComplete="tel"
                            maxLength={PHONE_MAX_LENGTH}
                        />
                        <FieldError field="phone" />
                    </div>
                    <div className="oms-form-group" data-staff-field="email">
                        <label className="oms-form-label">Email</label>
                        <input
                            type="text"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            placeholder="Enter email"
                            autoComplete="email"
                        />
                        <FieldError field="email" />
                    </div>
                    <div className="oms-form-group" data-staff-field="password">
                        <label className="oms-form-label">Password</label>
                        <div className="staff-password-wrapper">
                            <input
                                type={showPassword ? "text" : "password"}
                                name="password"
                                value={formData.password}
                                onChange={handleInputChange}
                                placeholder={
                                    isEditMode
                                        ? "Leave blank to keep current password"
                                        : "Enter password"
                                }
                                autoComplete={isEditMode ? "new-password" : "new-password"}
                            />
                            <button
                                type="button"
                                className="staff-password-toggle"
                                onClick={() => setShowPassword((prev) => !prev)}
                                aria-label={showPassword ? "Hide password" : "Show password"}
                            >
                                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        </div>
                        <FieldError field="password" />
                    </div>
                    <div className="oms-form-group" data-staff-field="emergency_contact_phone">
                        <label className="oms-form-label">Emergency Contact</label>
                        <input
                            type="text"
                            name="emergency_contact_phone"
                            value={formData.emergency_contact_phone}
                            onChange={handleInputChange}
                            placeholder="Enter 10-digit emergency contact"
                            inputMode="numeric"
                            autoComplete="tel"
                            maxLength={PHONE_MAX_LENGTH}
                        />
                        <FieldError field="emergency_contact_phone" />
                    </div>
                </div>

                <div className="product-form-page__section">
                    <h2 className="product-form-page__section-title">Employment</h2>
                    <p className="product-form-page__section-desc">Role, department, schedule, and office details.</p>
                </div>
                <div className="oms-form-flex">
                    <div className="oms-form-group" data-staff-field="job_title">
                        <label className="oms-form-label">Job Title</label>
                        <input
                            type="text"
                            name="job_title"
                            value={formData.job_title}
                            onChange={handleInputChange}
                            placeholder="Enter job title"
                        />
                        <FieldError field="job_title" />
                    </div>
                    <div className="oms-form-group" data-staff-field="department">
                        <label className="oms-form-label">Department</label>
                        <Select
                            name="department"
                            value={
                                formData.department
                                    ? { value: formData.department, label: formData.department }
                                    : null
                            }
                            options={department
                                .filter((dep) => dep.status === "Active") // ✅ only active departments
                                .map((dep) => ({
                                    value: dep.department_name,
                                    label: dep.department_name
                                }))
                            }
                            onChange={(opt) => {
                                setFormData((prev) => ({
                                    ...prev,
                                    department: opt?.value || "",
                                }));
                                clearFieldError("department");
                            }}
                            isClearable
                        />
                        <FieldError field="department" />
                    </div>


                    <div className="oms-form-group" data-staff-field="dof_joining">
                        <label className="oms-form-label">Date of Joining</label>
                        <input
                            type="date"
                            name="dof_joining"
                            value={formData.dof_joining}
                            onChange={handleInputChange}
                        />
                        <FieldError field="dof_joining" />
                    </div>
                    <div className="oms-form-group" data-staff-field="employ_status">
                        <label className="oms-form-label">Employee Status</label>
                        <Select
                            name="status"
                            value={
                                formData.employ_status
                                    ? { value: formData.employ_status, label: formData.employ_status }
                                    : null
                            }
                            options={[
                                { value: "Active", label: "Active" },
                                { value: "Inactive", label: "Inactive" },
                                { value: "Probation", label: "Probation" },
                            ]}
                            onChange={(opt) => {
                                setFormData((prev) => ({ ...prev, employ_status: opt?.value || "" }));
                                clearFieldError("employ_status");
                            }}
                        />
                        <FieldError field="employ_status" />
                    </div>
                    <div className="oms-form-group">
                        <label className="oms-form-label">Reporting manager</label>
                        <input
                            type="text"
                            name="report_manager"
                            value={formData.report_manager}
                            onChange={handleInputChange}
                            placeholder="Enter reporting manager"
                        />
                    </div>
                    <div className="oms-form-group">
                        <label className="oms-form-label">Office Location</label>
                        <input
                            type="text"
                            name="office_location"
                            value={formData.office_location}
                            onChange={handleInputChange}
                            placeholder="Enter office location"
                        />
                    </div>
                    <div className="oms-form-group">
                        <label className="oms-form-label">Working hours</label>
                        <div className="staff-time-row">
                            <div>
                                <label className="oms-form-label" htmlFor="start_time">
                                    Start
                                </label>
                                <input
                                    id="start_time"
                                    type="time"
                                    name="start_time"
                                    value={formData.start_time}
                                    onChange={handleInputChange}
                                />
                            </div>
                            <div>
                                <label className="oms-form-label" htmlFor="end_time">
                                    End
                                </label>
                                <input
                                    id="end_time"
                                    type="time"
                                    name="end_time"
                                    value={formData.end_time}
                                    onChange={handleInputChange}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="product-form-page__section">
                    <h2 className="product-form-page__section-title">Professional</h2>
                    <p className="product-form-page__section-desc">All fields in this section are required.</p>
                </div>
                <div className="oms-form-flex">
                    <div className="oms-form-group" data-staff-field="qualifications">
                        <label className="oms-form-label">Qualifications</label>
                        <input
                            type="text"
                            name="qualifications"
                            value={formData.qualifications}
                            onChange={handleInputChange}
                            placeholder="Enter qualifications"
                        />
                        <FieldError field="qualifications" />
                    </div>
                    <div className="oms-form-group" data-staff-field="prev_exp">
                        <label className="oms-form-label">Experience</label>
                        <input
                            type="text"
                            name="prev_exp"
                            value={formData.prev_exp}
                            onChange={handleInputChange}
                            placeholder="Enter previous experience"
                        />
                        <FieldError field="prev_exp" />
                    </div>

                    <div className="oms-form-group" data-staff-field="skills">
                        <label className="oms-form-label">Skills</label>
                        <input
                            type="text"
                            className="staff-multi-input"
                            value={skillInput}
                            onChange={(e) => {
                                setSkillInput(e.target.value);
                                clearFieldError("skills");
                            }}
                            onKeyDown={handleSkillKeyDown}
                            placeholder="Type a skill, then comma or Enter"
                        />
                        <FieldError field="skills" />
                        <div className="staff-chip-list">
                            {skills.map((skill, index) => (
                                <span key={index} className="staff-chip">
                                    {skill}
                                    <button
                                        type="button"
                                        onClick={() => removeSkill(index)}
                                        aria-label={`Remove ${skill}`}
                                    >
                                        <X size={14} />
                                    </button>
                                </span>
                            ))}
                        </div>
                    </div>

                    <div className="oms-form-group">
                        <label className="oms-form-label">Languages known</label>
                        <input
                            type="text"
                            className="staff-multi-input"
                            value={languageInput}
                            onChange={(e) => {
                                setLanguageInput(e.target.value);
                                clearFieldError("language_known");
                            }}
                            onKeyDown={handleLanguageKeyDown}
                            placeholder="Type a language, then comma or Enter"
                        />
                        <FieldError field="language_known" />
                        <div className="staff-chip-list">
                            {language_known.map((lang, index) => (
                                <span key={index} className="staff-chip">
                                    {lang}
                                    <button
                                        type="button"
                                        onClick={() => removeLanguage(index)}
                                        aria-label={`Remove ${lang}`}
                                    >
                                        <X size={14} />
                                    </button>
                                </span>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="product-form-page__section">
                    <h2 className="product-form-page__section-title">Payroll</h2>
                    <p className="product-form-page__section-desc">Account, IFSC, and bank name are required. PF number is optional.</p>
                </div>
                <div className="oms-form-flex">
                    <div className="oms-form-group" data-staff-field="account_no">
                        <label className="oms-form-label">Account No</label>
                        <input
                            type="text"
                            name="account_no"
                            value={formData.account_no}
                            onChange={handleInputChange}
                            placeholder="Enter account no (digits only)"
                            inputMode="numeric"
                            maxLength={18}
                        />
                        <FieldError field="account_no" />
                    </div>
                    <div className="oms-form-group" data-staff-field="ifsc">
                        <label className="oms-form-label">IFSC Code</label>
                        <input
                            type="text"
                            name="ifsc"
                            value={formData.ifsc}
                            onChange={handleInputChange}
                            placeholder="Enter IFSC"
                            maxLength={11}
                        />
                        <FieldError field="ifsc" />
                    </div>

                    <div className="oms-form-group" data-staff-field="bank">
                        <label className="oms-form-label">Bank Name</label>
                        <input
                            type="text"
                            name="bank"
                            value={formData.bank}
                            onChange={handleInputChange}
                            placeholder="Enter bank name"
                        />
                        <FieldError field="bank" />
                    </div>

                    <div className="oms-form-group">
                        <label className="oms-form-label">Provident Fund No</label>
                        <input
                            type="text"
                            name="pfNo"
                            value={formData.pfNo}
                            onChange={handleInputChange}
                            placeholder="Enter PF number"
                        />
                    </div>

                </div>

                <div className="product-form-page__section">
                    <h2 className="product-form-page__section-title">Other</h2>
                    <p className="product-form-page__section-desc">Personal details, hobbies, and weekly holidays.</p>
                </div>
                <div className="oms-form-flex">
                    <div className="oms-form-group">
                        <label className="oms-form-label">Marital Status</label>
                        <Select
                            name="marital_status"
                            value={
                                formData.marital_status
                                    ? { value: formData.marital_status, label: formData.marital_status }
                                    : null
                            }
                            options={[
                                { value: "Single", label: "Single" },
                                { value: "Married", label: "Married" },
                                { value: "Other", label: "Other" },
                            ]}
                            onChange={(opt) =>
                                setFormData((prev) => ({
                                    ...prev,
                                    marital_status: opt?.value || "",
                                }))
                            }
                        />
                    </div>

                    <div className="oms-form-group">
                        <label className="oms-form-label">Hobbies</label>
                        <input
                            type="text"
                            className="staff-multi-input"
                            value={hobbyInput}
                            onChange={(e) => setHobbyInput(e.target.value)}
                            onKeyDown={handleHobbyKeyDown}
                            placeholder="Type a hobby, then comma or Enter"
                        />
                        <div className="staff-chip-list">
                            {hobbies_interest.map((hobby, index) => (
                                <span key={index} className="staff-chip">
                                    {hobby}
                                    <button
                                        type="button"
                                        onClick={() => removeHobbies(index)}
                                        aria-label={`Remove ${hobby}`}
                                    >
                                        <X size={14} />
                                    </button>
                                </span>
                            ))}
                        </div>
                    </div>

                    <div className="oms-form-group">
                        <label className="oms-form-label">Weekly Holiday</label>
                        <Select
                            isMulti
                            options={daysOptions}
                            value={selectedValues}
                            onChange={handleChange}
                            placeholder="Select weekly holidays"
                        />
                    </div>
                </div>

                <div className="oms-form-actions">
                    {loading ? (
                        <button type="button" className="post-property-submit" disabled>
                            Saving…
                        </button>
                    ) : (
                        <button type="submit" className="post-property-submit">
                            {isEditMode ? "Update" : "Save"}
                        </button>
                    )}
                </div>
            </div>
            </form>
            <ToastContainer
                position="top-center"
                autoClose={4000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                style={{ zIndex: 999999 }}
            />
        </div>
    );
};

export default NewStaff;
